'use client'

import { useEffect, useMemo, useState } from 'react'
import { format, subDays, subMonths } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { HugeiconsIcon } from '@hugeicons/react'
import { Calendar04Icon } from '@hugeicons-pro/core-solid-rounded'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

export type DatePreset = 'last_7' | 'last_30' | 'last_90' | 'last_12_months' | 'custom'

export interface DateRangeValue {
  preset: DatePreset
  from: Date
  to: Date
}

export interface DateRangeFilterProps {
  value: DateRangeValue
  onChange: (value: DateRangeValue) => void
  /** Visual size hint; defaults to 'sm'. */
  size?: 'sm' | 'md'
  /** Optional className for the trigger button. */
  className?: string
}

const STORAGE_KEY = 'monarch.dateRange'

const PRESETS: Array<{ preset: Exclude<DatePreset, 'custom'>; label: string; getRange: (now: Date) => { from: Date; to: Date } }> = [
  { preset: 'last_7', label: 'Last 7 days', getRange: (now) => ({ from: subDays(now, 7), to: now }) },
  { preset: 'last_30', label: 'Last 30 days', getRange: (now) => ({ from: subDays(now, 30), to: now }) },
  { preset: 'last_90', label: 'Last 90 days', getRange: (now) => ({ from: subDays(now, 90), to: now }) },
  { preset: 'last_12_months', label: 'Last 12 months', getRange: (now) => ({ from: subMonths(now, 12), to: now }) },
]

const calendarClassNames = {
  months: "flex flex-col gap-4",
  month: "flex flex-col gap-4",
  caption: "flex justify-center pt-1 relative items-center w-full",
  caption_label: "text-sm font-medium text-atrium-black dark:text-white",
  nav: "flex items-center gap-1",
  nav_button: cn(
    'h-7 w-7 rounded-md border border-atrium-border bg-white p-0 text-atrium-black/60 dark:text-white/60 opacity-60 hover:bg-atrium-content dark:hover:bg-white/10 hover:opacity-100'
  ),
  nav_button_previous: "absolute left-1",
  nav_button_next: "absolute right-1",
  table: "w-full border-collapse",
  head_row: "flex",
  head_cell: "text-atrium-black/50 dark:text-white/50 w-9 font-normal text-[0.8rem] text-center",
  row: "flex w-full mt-1",
  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
  day: cn("h-9 w-9 p-0 font-normal text-atrium-black dark:text-white hover:bg-atrium-content dark:hover:bg-white/10 rounded-full flex items-center justify-center aria-selected:opacity-100"),
  day_today: "font-semibold text-accent-600 dark:text-accent-400",
  day_outside: "text-atrium-black/30 dark:text-white/30",
  day_disabled: "text-atrium-black/30 dark:text-white/30 cursor-not-allowed",
  day_selected: "bg-accent-600 text-white hover:bg-accent-700 focus:bg-accent-600 focus:text-white rounded-full",
  day_range_start: "bg-accent-600 text-white hover:bg-accent-700 rounded-full",
  day_range_end: "bg-accent-600 text-white hover:bg-accent-700 rounded-full",
  day_range_middle: "bg-accent-100 dark:bg-accent-900/30 text-accent-900 dark:text-accent-100 rounded-full",
  day_hidden: "invisible",
}

export function DateRangeFilter({
  value,
  onChange,
  size = 'sm',
  className,
}: DateRangeFilterProps): JSX.Element {
  const [desktopOpen, setDesktopOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [pendingDateRange, setPendingDateRange] = useState<DateRange>({ from: value.from, to: value.to })

  useEffect(() => {
    setPendingDateRange({ from: value.from, to: value.to })
  }, [value.from, value.to])

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const restored = parseStoredValue(raw)
      onChange(restored ?? buildPresetValue('last_30'))
    } catch {
      onChange(buildPresetValue('last_30'))
    }
    // Restore exactly once on mount. Parent onChange is expected to be stable enough
    // for this controlled filter; including it here would repeatedly reset user input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        preset: value.preset,
        from: value.from.toISOString(),
        to: value.to.toISOString(),
      }))
    } catch {
      /* ignore */
    }
  }, [value])

  const dateRangeLabel = useMemo(
    () => `${format(value.from, 'MMM d')} – ${format(value.to, 'MMM d')}`,
    [value.from, value.to],
  )
  const presetLabel = PRESETS.find((preset) => preset.preset === value.preset)?.label
  const buttonLabel = value.preset === 'custom' ? dateRangeLabel : presetLabel ?? 'Last 30 days'
  const isCustom = value.preset === 'custom'
  const triggerClassName = cn(
    'gap-1 sm:gap-1.5 text-xs font-medium min-h-[44px] sm:min-h-0',
    size === 'md' ? 'h-11 sm:h-9 px-2.5 sm:px-3' : 'h-11 sm:h-7 px-1.5 sm:px-2',
    className,
  )
  const triggerContent = (
    <>
      <HugeiconsIcon icon={Calendar04Icon} className="size-3.5 shrink-0 text-atrium-black/60" />
      <span className="max-w-[120px] truncate sm:max-w-none">{buttonLabel}</span>
    </>
  )

  function resetPendingToCurrent(): void {
    if (isCustom) setPendingDateRange({ from: value.from, to: value.to })
  }

  function handlePresetChange(preset: Exclude<DatePreset, 'custom'>): void {
    const next = buildPresetValue(preset)
    onChange(next)
    setDesktopOpen(false)
    setMobileOpen(false)
  }

  function handleCustomClick(): void {
    onChange({ preset: 'custom', from: value.from, to: value.to })
    setPendingDateRange({ from: value.from, to: value.to })
  }

  function handleClear(): void {
    const next = buildPresetValue('last_30')
    setPendingDateRange({ from: next.from, to: next.to })
    onChange(next)
    setDesktopOpen(false)
    setMobileOpen(false)
  }

  function handleApply(): void {
    const from = pendingDateRange.from
    if (!from) return
    onChange({
      preset: 'custom',
      from,
      to: pendingDateRange.to ?? from,
    })
    setDesktopOpen(false)
    setMobileOpen(false)
  }

  return (
    <>
      <Popover open={desktopOpen} onOpenChange={(nextOpen) => {
        setDesktopOpen(nextOpen)
        if (nextOpen) resetPendingToCurrent()
      }}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className={cn('hidden sm:inline-flex', triggerClassName)}>
            {triggerContent}
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn('p-0', isCustom ? 'w-[650px]' : 'w-[180px]')} align="end">
          <DateRangeFilterContent
            value={value}
            isCustom={isCustom}
            pendingDateRange={pendingDateRange}
            onPendingChange={setPendingDateRange}
            onPresetChange={handlePresetChange}
            onCustomClick={handleCustomClick}
            onClear={handleClear}
            onApply={handleApply}
            numberOfMonths={2}
            presetButtonClassName="py-1.5"
            calendarMonthsClassName="flex flex-row gap-4"
          />
        </PopoverContent>
      </Popover>

      <Sheet open={mobileOpen} onOpenChange={(nextOpen) => {
        setMobileOpen(nextOpen)
        if (nextOpen) resetPendingToCurrent()
      }}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="sm" className={cn('inline-flex sm:hidden', triggerClassName)}>
            {triggerContent}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[88vh] gap-0 overflow-y-auto rounded-t-[18px] border-atrium-border bg-white p-0 dark:bg-atrium-inner">
          <DateRangeFilterContent
            value={value}
            isCustom={isCustom}
            pendingDateRange={pendingDateRange}
            onPendingChange={setPendingDateRange}
            onPresetChange={handlePresetChange}
            onCustomClick={handleCustomClick}
            onClear={handleClear}
            onApply={handleApply}
            numberOfMonths={1}
            presetButtonClassName="min-h-[44px] py-3"
            calendarMonthsClassName="flex flex-col gap-4"
          />
        </SheetContent>
      </Sheet>
    </>
  )
}

function DateRangeFilterContent({
  value,
  isCustom,
  pendingDateRange,
  onPendingChange,
  onPresetChange,
  onCustomClick,
  onClear,
  onApply,
  numberOfMonths,
  presetButtonClassName,
  calendarMonthsClassName,
}: {
  value: DateRangeValue
  isCustom: boolean
  pendingDateRange: DateRange
  onPendingChange: (range: DateRange) => void
  onPresetChange: (preset: Exclude<DatePreset, 'custom'>) => void
  onCustomClick: () => void
  onClear: () => void
  onApply: () => void
  numberOfMonths: 1 | 2
  presetButtonClassName: string
  calendarMonthsClassName: string
}): JSX.Element {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-0.5 p-2">
        {PRESETS.map((preset) => (
          <button
            key={preset.preset}
            type="button"
            onClick={() => onPresetChange(preset.preset)}
            className={cn(
              'rounded-md px-2 text-left text-sm text-atrium-black transition-colors hover:bg-atrium-content dark:text-white',
              presetButtonClassName,
              value.preset === preset.preset && 'bg-accent-50 font-medium text-accent-700 dark:bg-accent-950 dark:text-accent-300',
            )}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onCustomClick}
          className={cn(
            'rounded-md px-2 text-left text-sm text-atrium-black transition-colors hover:bg-atrium-content dark:text-white',
            presetButtonClassName,
            isCustom && 'bg-accent-50 font-medium text-accent-700 dark:bg-accent-950 dark:text-accent-300',
          )}
        >
          Custom range
        </button>
      </div>

      {isCustom && (
        <>
          <div className="border-t border-atrium-border px-2 pb-2">
            <Calendar
              mode="range"
              selected={pendingDateRange}
              onSelect={(range) => onPendingChange(range ?? { from: undefined, to: undefined })}
              disabled={(date) => {
                const today = new Date()
                today.setHours(23, 59, 59, 999)
                return date > today
              }}
              numberOfMonths={numberOfMonths}
              className="w-full"
              classNames={{
                ...calendarClassNames,
                months: calendarMonthsClassName,
              }}
            />
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-atrium-border p-3">
            <Button variant="ghost" size="sm" className="min-h-[44px] sm:min-h-0" onClick={onClear}>
              Clear
            </Button>
            <Button size="sm" className="min-h-[44px] sm:min-h-0" onClick={onApply}>
              Apply
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

function buildPresetValue(preset: Exclude<DatePreset, 'custom'>): DateRangeValue {
  const now = new Date()
  const match = PRESETS.find((item) => item.preset === preset) ?? PRESETS[1]
  const range = match.getRange(now)
  return { preset: match.preset, from: range.from, to: range.to }
}

function parseStoredValue(raw: string): DateRangeValue | null {
  const parsed = JSON.parse(raw) as { preset?: string; from?: string; to?: string }
  const from = parsed.from ? new Date(parsed.from) : null
  const to = parsed.to ? new Date(parsed.to) : null
  if (!from || Number.isNaN(from.getTime()) || !to || Number.isNaN(to.getTime())) return null

  if (parsed.preset === 'custom') return { preset: 'custom', from, to }
  const preset = PRESETS.find((item) => item.preset === parsed.preset)?.preset
  if (!preset) return null
  return buildPresetValue(preset)
}
